import { supabase } from '@/lib/supabase';
import { logInfo, logError, logDebug, logWarn } from '@/utils/logger';
import { isUserAdmin } from './utils';

/**
 * Get system-wide statistics for admin users
 * 
 * @param adminId - ID of the user requesting the stats (must be admin)
 * @returns Stats object containing various system metrics
 */
export const getSystemStats = async (adminId: string) => {
  try {
    logInfo("Fetching system statistics", "adminOperations", { adminId });
    
    // Check if the user is actually an admin
    const isAdmin = await isUserAdmin(adminId);
    if (!isAdmin) {
      logWarn("Unauthorized admin access attempt", "adminOperations", { userId: adminId });
      return {
        data: null,
        error: "Unauthorized. Admin role required.",
        status: 403
      };
    }
    
    // Since we don't actually have the admin_stats table in the database structure,
    // we're returning mock data for this example. In a real app, replace this with
    // actual database queries.
    
    // In a production app, this might be a call to:
    // const { data, error } = await supabase.rpc('get_system_stats');
    
    logDebug("Returning mock system stats data", "adminOperations");
    
    // Mock data that will be properly typed
    const mockData = {
      totalDetections: 1284,
      highRiskDetections: 237,
      dailyDetections: {
        '2025-04-07': 42,
        '2025-04-08': 56,
        '2025-04-09': 38,
        '2025-04-10': 71,
        '2025-04-11': 63,
        '2025-04-12': 49,
        '2025-04-13': 57,
        '2025-04-14': 28 // today (partial)
      }
    };
    
    return {
      data: mockData,
      error: null,
      status: 200
    };
  } catch (e) {
    const error = e as Error;
    logError("Error fetching system stats", "adminOperations", { error: error.message });
    return {
      data: null,
      error: `Failed to retrieve system statistics: ${error.message}`,
      status: 500
    };
  }
};

/**
 * Get audit logs for admin users
 */
export const getAuditLogs = async (adminId: string, limit = 10, offset = 0) => {
  try {
    logInfo("Fetching audit logs", "adminOperations", { adminId, limit, offset });
    
    // Check if user is admin
    const isAdmin = await isUserAdmin(adminId);
    if (!isAdmin) {
      logWarn("Unauthorized audit logs access attempt", "adminOperations", { userId: adminId });
      return {
        data: null,
        error: "Unauthorized. Admin role required.",
        status: 403,
        count: 0
      };
    }
    
    // For now, returning mock data since we don't have an audit_logs table
    // In a real app, this would query from a real table
    
    // Generate mock audit logs based on the requested page
    const totalMockLogs = 100; // Total number of logs in our "database"
    const startIndex = offset;
    const endIndex = Math.min(offset + limit, totalMockLogs);
    
    const mockLogs = [];
    for (let i = startIndex; i < endIndex; i++) {
      mockLogs.push({
        event_id: `event-${1000 + i}`,
        user_id: `user-${Math.floor(Math.random() * 10) + 1}`,
        timestamp: new Date(Date.now() - i * 3600000).toISOString(),
        action: ['login', 'logout', 'create_analysis', 'view_report', 'export_data'][Math.floor(Math.random() * 5)],
        details: `Event details for mock audit log ${i + 1}`
      });
    }
    
    logDebug("Returning mock audit logs", "adminOperations", { 
      count: mockLogs.length, 
      totalAvailable: totalMockLogs
    });
    
    return {
      data: mockLogs,
      count: totalMockLogs,
      error: null,
      status: 200
    };
  } catch (e) {
    const error = e as Error;
    logError("Error fetching audit logs", "adminOperations", { error: error.message });
    return {
      data: null,
      count: 0,
      error: `Failed to retrieve audit logs: ${error.message}`,
      status: 500
    };
  }
};
